/**
 * Operator Console — Theme MUI
 *
 * Direzione aesthetica: "Console Editoriale Telemetrica".
 * - Sans body raffinato (Manrope, non Inter) + display Fraunces variabile
 *   con WONK e SOFT axes per personalita' tipografica
 * - Mono JetBrains per dati tecnici (ID Firestore, importi, timestamp)
 * - Sfondo `Bone` (#FAFAF7) — bianco caldo che non sembra ospedale
 * - Inchiostro `Graphite` (#161616) — non puro nero
 * - Tier accents distinte (slate / cobalto / viola / ambra) come "tessere"
 * - Tabular numbers ovunque ci siano metriche → allineamento perfetto
 *
 * @since 2026-05-18 (refactor MOD-5 dashboard)
 */

import { createTheme, alpha } from '@mui/material/styles';

// ============================================================================
// DESIGN TOKENS — Esportati per uso diretto nei componenti
// ============================================================================

export const tokens = {
  color: {
    bone: '#FAFAF7',
    paper: '#FFFFFF',
    graphite: '#161616',
    ash: '#52525B',
    mist: '#A1A1AA',
    line: '#E4E4E7',
    lineSoft: '#F4F4F5',
    tierFree: { main: '#52525B', light: '#71717A', dim: alpha('#52525B', 0.08) },
    tierPro: { main: '#2563EB', light: '#3B82F6', dim: alpha('#2563EB', 0.08) },
    tierBusiness: { main: '#7C3AED', light: '#9333EA', dim: alpha('#7C3AED', 0.08) },
    tierCustom: { main: '#D97706', light: '#F59E0B', dim: alpha('#D97706', 0.08) },
    success: '#10B981',
    danger: '#DC2626',
    warning: '#D97706',
    info: '#0EA5E9',
  },
  font: {
    display: '"Fraunces", "Times New Roman", Georgia, serif',
    body: '"Manrope", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
  },
  radius: {
    xs: 4,
    sm: 6,
    md: 10,
    lg: 14,
    pill: 999,
  },
  shadow: {
    card: '0 1px 0 rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)',
    elevated: '0 4px 12px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    hover: '0 8px 24px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
  },
};

const CSS_VARS = `
:root {
  --c-bone: ${tokens.color.bone};
  --c-paper: ${tokens.color.paper};
  --c-graphite: ${tokens.color.graphite};
  --c-ash: ${tokens.color.ash};
  --c-mist: ${tokens.color.mist};
  --c-line: ${tokens.color.line};
  --c-line-soft: ${tokens.color.lineSoft};
  --c-tier-free: ${tokens.color.tierFree.main};
  --c-tier-pro: ${tokens.color.tierPro.main};
  --c-tier-business: ${tokens.color.tierBusiness.main};
  --c-tier-custom: ${tokens.color.tierCustom.main};
  --f-display: ${tokens.font.display};
  --f-body: ${tokens.font.body};
  --f-mono: ${tokens.font.mono};
}
html, body {
  background-color: var(--c-bone);
  color: var(--c-graphite);
}
body {
  font-feature-settings: 'cv11', 'ss01', 'kern';
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.num, .num *, [data-num] {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1, 'ss01' 1;
}
.mono, [data-mono] {
  font-family: var(--f-mono);
  font-feature-settings: 'tnum' 1;
}
@keyframes tier-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.tenant-row__name {
  position: relative;
  display: inline-block;
}
.tenant-row__name::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 0;
  height: 1.5px;
  background: var(--c-graphite);
  transition: width 220ms cubic-bezier(0.2, 0.7, 0.3, 1);
}
.tenant-row:hover .tenant-row__name::after {
  width: 100%;
}
::selection {
  background-color: ${alpha(tokens.color.tierBusiness.main, 0.18)};
  color: var(--c-graphite);
}
::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: ${alpha(tokens.color.mist, 0.4)};
  border-radius: 8px;
  border: 2px solid ${tokens.color.bone};
}
::-webkit-scrollbar-thumb:hover { background: ${alpha(tokens.color.ash, 0.6)}; }
`;

if (typeof document !== 'undefined' && !document.getElementById('console-design-tokens')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'console-design-tokens';
  styleEl.textContent = CSS_VARS;
  document.head.appendChild(styleEl);
}

const PLATFORM_BAR_BG = tokens.color.graphite;
const PRIMARY = tokens.color.tierBusiness.main;
const SECONDARY = tokens.color.tierPro.main;

export const consoleTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: PRIMARY },
    secondary: { main: SECONDARY },
    success: { main: tokens.color.success },
    error: { main: tokens.color.danger },
    warning: { main: tokens.color.warning },
    info: { main: tokens.color.info },
    text: {
      primary: tokens.color.graphite,
      secondary: tokens.color.ash,
      disabled: tokens.color.mist,
    },
    divider: tokens.color.line,
    background: {
      default: tokens.color.bone,
      paper: tokens.color.paper,
    },
  },
  typography: {
    fontFamily: tokens.font.body,
    h1: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      fontVariationSettings: '"WONK" 0, "SOFT" 30, "opsz" 144',
    },
    h2: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      letterSpacing: '-0.018em',
      fontVariationSettings: '"WONK" 0, "SOFT" 30, "opsz" 96',
    },
    h3: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      letterSpacing: '-0.015em',
      fontVariationSettings: '"WONK" 0, "SOFT" 40, "opsz" 72',
    },
    h4: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      letterSpacing: '-0.012em',
      fontVariationSettings: '"WONK" 0, "SOFT" 50, "opsz" 48',
    },
    h5: { fontWeight: 700, letterSpacing: '-0.005em' },
    h6: { fontWeight: 700, letterSpacing: '-0.003em' },
    overline: {
      fontSize: '0.68rem',
      letterSpacing: '0.12em',
      fontWeight: 700,
      color: tokens.color.mist,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: tokens.radius.md },
  transitions: {
    duration: {
      shortest: 120,
      shorter: 180,
      short: 220,
      standard: 260,
      complex: 320,
      enteringScreen: 220,
      leavingScreen: 180,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: PLATFORM_BAR_BG,
          color: '#fff',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.color.paper,
          borderRight: `1px solid ${tokens.color.line}`,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontWeight: 600,
          padding: '6px 14px',
          transition: 'all 180ms cubic-bezier(0.2, 0.7, 0.3, 1)',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: tokens.shadow.elevated },
        },
        outlined: {
          borderColor: tokens.color.line,
          color: tokens.color.graphite,
          '&:hover': {
            borderColor: tokens.color.graphite,
            backgroundColor: alpha(tokens.color.graphite, 0.04),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: tokens.shadow.card,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.color.line}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: tokens.color.line,
          borderRadius: tokens.radius.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: tokens.radius.sm,
          letterSpacing: '0.01em',
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.7rem',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          marginInline: 6,
          '&.Mui-selected, &.active': {
            backgroundColor: alpha(PRIMARY, 0.10),
            color: PRIMARY,
            '& .MuiListItemIcon-root': { color: PRIMARY },
            '&:hover': { backgroundColor: alpha(PRIMARY, 0.14) },
          },
        },
      },
    },
    /*
      TELEFONO (18/10/2026). Le tabelle della console hanno da 4 a 8 colonne e sotto i 900 px allargavano tutta la
      pagina, che scorreva di lato insieme al menu e ai titoli. Qui, in un punto solo per tutte le pagine, sotto i
      900 px è la TABELLA a scorrere di lato dentro il suo riquadro, e le intestazioni restano su una riga. Sul
      computer non cambia niente. Le pagine che sul telefono meritano schede invece di righe (i questionari) le
      disegnano da sé.
    */
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            display: 'block',
            overflowX: 'auto',
            maxWidth: '100%',
            '& .MuiTableCell-head': { whiteSpace: 'nowrap' },
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: '0.68rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tokens.color.mist,
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${tokens.color.line}`,
          paddingTop: 10,
          paddingBottom: 10,
        },
        body: {
          borderBottom: `1px solid ${tokens.color.lineSoft}`,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          letterSpacing: '-0.005em',
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: tokens.radius.lg } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: tokens.radius.sm } },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontSize: '0.875rem',
          '& fieldset': { borderColor: tokens.color.line },
          '&:hover fieldset': { borderColor: tokens.color.ash + ' !important' },
        },
      },
    },
  },
});

export const CONSOLE_PLATFORM_ACCENT = PLATFORM_BAR_BG;
